import React, { useEffect, useState } from "react";
import { useFamily } from "../../domain/services/hooks/useFamily";
import { FirestoreFamilyRepository } from "../../infrastructure/repositories/FamilyRepository";
import { ProviderUser } from "../../domain/types/ProviderUser";
import * as Icon from "react-feather";
import LoadingState from "../../components/common/LoadingState";
import ErrorState from "../../components/common/ErrorState";
import { useUser } from "../../contexts/UserContext";
import { Button, Modal, TextInput, Alert } from "flowbite-react"; // Import Alert component
import {
  sendFamilyInvitation,
  getFamilyInvitations,
} from "../../domain/services/FamilyInvitationService";
import { FamilyInvitation } from "../../domain/types/FamilyInvitation";
import { HiUserGroup } from "react-icons/hi";

const FamilyPage: React.FC = () => {
  const { family, loading, error } = useFamily();
  const { appUser, currentUser } = useUser();
  const [familyMembers, setFamilyMembers] = useState<
    Record<string, ProviderUser>
  >({});
  const [membersLoading, setMembersLoading] = useState<boolean>(true);

  // State for modal visibility and email input
  const [isInviteModalOpen, setIsInviteModalOpen] = useState<boolean>(false);
  const [inviteEmail, setInviteEmail] = useState<string>("");
  const [isInviting, setIsInviting] = useState<boolean>(false);
  const [inviteError, setInviteError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  // State for invitations
  const [invitations, setInvitations] = useState<FamilyInvitation[]>([]);
  const [_invitationsLoading, setInvitationsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchFamilyMembers = async () => {
      if (family) {
        const familyRepo = new FirestoreFamilyRepository();
        try {
          const members = await familyRepo.getFamilyMembers(family.familyId);
          setFamilyMembers(members);
        } catch (err) {
          console.error("Error fetching family members:", err);
        } finally {
          setMembersLoading(false);
        }
      } else {
        setMembersLoading(false);
      }
    };

    fetchFamilyMembers();
  }, [family]);

  // Fetch invitations
  useEffect(() => {
    const fetchInvitations = async () => {
      if (family) {
        try {
          const invitations = await getFamilyInvitations(family.familyId);
          setInvitations(invitations);
        } catch (err) {
          console.error("Error fetching invitations:", err);
        } finally {
          setInvitationsLoading(false);
        }
      } else {
        setInvitationsLoading(false);
      }
    };

    fetchInvitations();
  }, [family]);

  if (loading || membersLoading || !appUser || !currentUser) {
    return <LoadingState message="Loading family data..." />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  if (!family) {
    return (
      <div className="p-4">
        <div className="bg-blue-100 border border-blue-400 text-blue-700 p-4 rounded relative">
          <strong className="font-bold">No Family Data</strong>
          <span className="block sm:inline"> No family data available.</span>
        </div>
      </div>
    );
  }

  // Calculate the number of family members
  const familyMemberCount = Object.keys(familyMembers).length;

  const handleRemoveMember = (_appUserId: string) => {
    // Implement the logic to remove the member from the family
    // You can use the familyId and memberId to remove the member from the family
  };
  const handleCancelInvitation = (_invitationId: string) => {
    // Implement the logic to remove the member from the family
    // You can use the familyId and memberId to remove the member from the family
  };

  // Handler for opening the invite modal
  const openInviteModal = () => {
    setInviteEmail("");
    setInviteError("");
    setSuccessMessage("");
    setIsInviteModalOpen(true);
  };

  // Handler for sending the invitation
  const handleSendInvitation = async () => {
    if (!inviteEmail) {
      setInviteError("Please enter an email address.");
      return;
    }

    const emailRegex = /\S+@\S+\.\S+/;
    if (!emailRegex.test(inviteEmail)) {
      setInviteError("Please enter a valid email address.");
      return;
    }

    setIsInviting(true);
    setInviteError("");
    setSuccessMessage("");

    try {
      // Send the invitation
      await sendFamilyInvitation(
        appUser.appUserId,
        inviteEmail,
        currentUser.email || "",
        family.familyId,
      );

      // Show success message
      setSuccessMessage(`Invitation sent to ${inviteEmail}`);

      // Close the modal after successful invitation
      setIsInviteModalOpen(false);

      // Refresh invitations list
      const updatedInvitations = await getFamilyInvitations(family.familyId);
      setInvitations(updatedInvitations);
    } catch (err) {
      console.error("Error sending invitation:", err);
      setInviteError("Failed to send invitation. Please try again.");
    } finally {
      setIsInviting(false);
    }
  };

  return (
    <>
      <section className="relative pb-16">
        <div className="container relative mx-auto px-4">
          <div className="rounded-xl shadow-lg dark:shadow-gray-800 bg-white dark:bg-slate-900">
            <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center p-6 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                  <HiUserGroup className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h5 className="text-xl font-semibold">Family Members</h5>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {familyMemberCount}/3 members connected
                  </p>
                </div>
              </div>

              {familyMemberCount < 3 && (
                <Button
                  onClick={openInviteModal}
                  className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-700 rounded-lg hover:from-indigo-700 hover:to-purple-800 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <Icon.Plus className="mr-2 flex-shrink-0" />
                  Invite Family Member
                </Button>
              )}
            </div>

            <div className="p-6">
              <div className="grid gap-6">
                {Object.entries(familyMembers).map(
                  ([appUserId, providerUser]) => (
                    <div
                      key={appUserId}
                      className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-6 bg-gray-50 dark:bg-slate-800/50 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <div className="flex items-center w-full sm:w-auto">
                        <img
                          src={providerUser.photoURL || "/default-avatar.png"}
                          alt={providerUser.displayName || "User Avatar"}
                          className="w-12 h-12 rounded-full ring-4 ring-white dark:ring-gray-700"
                        />
                        <div className="ml-4">
                          <h6 className="text-lg font-semibold flex items-center gap-2">
                            {providerUser.displayName || "Unknown User"}
                            {family.admin === appUserId && (
                              <span className="px-2 py-1 text-xs font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-700 rounded-full">
                                Admin
                              </span>
                            )}
                          </h6>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {providerUser.email || "Email not available"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mt-4 sm:mt-0">
                        {family.admin === appUserId ? (
                          <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                            Family Admin
                          </span>
                        ) : (
                          <Button
                            onClick={() => handleRemoveMember(appUserId)}
                            className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 dark:text-red-400 rounded-lg transition-colors"
                          >
                            <Icon.UserMinus className="w-4 h-4 mr-2" />
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>

            {invitations.length > 0 && (
              <div className="mt-8 border-t border-gray-100 dark:border-gray-800 pt-6">
                <h3 className="text-lg font-semibold mb-4">
                  Pending Invitations
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-slate-800/50">
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                          Email
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                          Sent By
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                          Date
                        </th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {invitations.map((invitation) => (
                        <tr
                          key={invitation.invitationId}
                          className="hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
                        >
                          <td className="px-4 py-3 text-sm">
                            {invitation.invitedEmail}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {invitation.inviterEmail}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-500">
                              Pending
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {invitation.createdAt.toDate().toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              onClick={() =>
                                handleCancelInvitation(invitation.invitationId)
                              }
                              className="text-sm text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                            >
                              Cancel
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <Modal
        show={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        size="md"
      >
        <Modal.Header className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white rounded-t-xl">
          <h3 className="text-xl font-semibold">Invite Family Member</h3>
        </Modal.Header>
        <Modal.Body className="p-6">
          {inviteError && (
            <Alert color="failure" className="mb-4">
              {inviteError}
            </Alert>
          )}
          {successMessage && (
            <Alert color="success" className="mb-4">
              {successMessage}
            </Alert>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email Address
              </label>
              <TextInput
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Enter email address"
                className="w-full"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button color="gray" onClick={() => setIsInviteModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSendInvitation}
                disabled={isInviting}
                className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white hover:from-indigo-700 hover:to-purple-800"
              >
                {isInviting ? "Sending..." : "Send Invitation"}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default FamilyPage;
